import * as React from 'react';

import { Avatar, AvatarFallback, AvatarImage } from './avatar';
import { cn } from './lib/utils';

interface UserInfoProps extends Omit<React.ComponentProps<'div'>, 'children'> {
  /** Full name. When omitted/null/blank, only the email is shown at full size. */
  name?: string | null;
  /** Email address. Subtext when a name is shown; primary line when alone. */
  email: string;
  /** Optional avatar image URL; falls back to initials when absent or fails to load. */
  avatarUrl?: string;
  /** Avatar size — matches the Avatar primitive. */
  size?: 'sm' | 'default' | 'lg';
}

function getInitials(source: string): string {
  return source
    .split(/\s+/)
    .map((part) => part.charAt(0))
    .filter(Boolean)
    .join('')
    .slice(0, 2)
    .toUpperCase();
}

function UserInfo({
  name,
  email,
  avatarUrl,
  size = 'default',
  className,
  ...props
}: UserInfoProps) {
  const trimmedName = name?.trim() ?? '';
  const hasName = trimmedName.length > 0;
  const initialsSource = hasName ? trimmedName : email;
  const initials = getInitials(initialsSource) || email.charAt(0).toUpperCase();
  const altText = hasName ? trimmedName : email;

  return (
    <div data-slot="user-info" className={cn('flex items-center gap-2', className)} {...props}>
      <Avatar size={size}>
        {avatarUrl ? <AvatarImage src={avatarUrl} alt={altText} /> : null}
        <AvatarFallback>{initials}</AvatarFallback>
      </Avatar>
      <div className="grid min-w-0 flex-1 text-start leading-tight">
        {hasName ? (
          <>
            <span className="truncate text-sm font-medium">{trimmedName}</span>
            <span className="text-muted-foreground truncate text-xs">{email}</span>
          </>
        ) : (
          <span className="truncate text-sm font-medium">{email}</span>
        )}
      </div>
    </div>
  );
}

export { UserInfo };
export type { UserInfoProps };
